import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import {
  SendAppointmentMessagePayload,
  SendAppointmentMessageResponse,
  PreviewMessageResponse,
} from '../types/appointment';

/**
 * Hook for previewing rendered appointment message
 */
export function usePreviewAppointmentMessage(
  clientId: string,
  appointmentId: string,
  messageType: 'confirmation' | 'reminder' | null
) {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['appointment-message-preview', appointmentId, messageType],
    enabled: Boolean(appointmentId && messageType && session),
    queryFn: () =>
      apiRequest<PreviewMessageResponse>(
        `/api/clients/${clientId}/appointments/${appointmentId}/preview-whatsapp?type=${messageType}`,
        { method: 'GET' },
        session?.access_token
      ),
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Hook for sending WhatsApp appointment message
 */
export function useSendAppointmentMessage(clientId: string, appointmentId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SendAppointmentMessagePayload) => {
      return apiRequest<SendAppointmentMessageResponse>(
        `/api/clients/${clientId}/appointments/${appointmentId}/send-whatsapp`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      // Invalidate appointment queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: ['appointments', clientId],
      });
      // Also invalidate preview queries
      queryClient.invalidateQueries({
        queryKey: ['appointment-message-preview', appointmentId],
      });
    },
  });
}
